/**
 * Dynamic Appointment Scheduling System
 * 
 * This module provides a capacity-based scheduling system that:
 * - Divides the day into 30-minute slots
 * - Tracks occupancy (0-3 cars) for each slot
 * - Handles duration-aware booking (blocks multiple slots)
 * - Prevents overbooking by checking all affected slots
 */

export interface Appointment {
  scheduledDate: string;
  scheduledTime: string;
  durationMinutes: number;
}

export interface SlotInfo {
  time: string;
  slotIndex: number;
  capacity: number;
  available: boolean;
  fullness: string;
}

/**
 * Convert time string (HH:mm) to slot index
 * @param time - Time in HH:mm format (e.g., "09:00", "14:30")
 * @returns Slot index (0-47 for 00:00-23:30)
 * 
 * @example
 * timeToSlotIndex("09:00") // 18
 * timeToSlotIndex("09:30") // 19
 * timeToSlotIndex("14:30") // 29
 */
export function timeToSlotIndex(time: string): number {
  const [hour, min] = time.split(":").map(Number);
  return hour * 2 + (min >= 30 ? 1 : 0);
}

/**
 * Convert slot index to time string
 * @param index - Slot index (0-47)
 * @returns Time in HH:mm format
 * 
 * @example
 * slotIndexToTime(18) // "09:00"
 * slotIndexToTime(19) // "09:30"
 */
export function slotIndexToTime(index: number): string {
  const hour = Math.floor(index / 2);
  const min = (index % 2) * 30;
  return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
}

/**
 * Get all slot indices affected by an appointment
 * @param startTime - Start time in HH:mm format
 * @param durationMinutes - Duration in minutes
 * @returns Array of affected slot indices
 * 
 * @example
 * // 2-hour service starting at 15:00
 * getAffectedSlots("15:00", 120)
 * // Returns: [30, 31, 32, 33] (15:00, 15:30, 16:00, 16:30)
 */
export function getAffectedSlots(startTime: string, durationMinutes: number): number[] {
  const startSlot = timeToSlotIndex(startTime);
  const numSlots = Math.ceil(durationMinutes / 30);
  return Array.from({ length: numSlots }, (_, i) => startSlot + i);
}

/**
 * Build daily occupancy map for a specific date
 * @param date - Date in YYYY-MM-DD format
 * @param appointments - List of appointments
 * @returns Map of slot index to car count
 * 
 * @example
 * buildDailyOccupancy("2025-10-28", appointments)
 * // Returns: Map { 18 => 2, 19 => 3, 20 => 1, ... }
 */
export function buildDailyOccupancy(
  date: string,
  appointments: Appointment[]
): Map<number, number> {
  const occupancyMap = new Map<number, number>();
  
  console.log(`📊 Building occupancy map for ${date}...`);
  
  for (const apt of appointments) {
    if (apt.scheduledDate !== date) continue;
    
    const affectedSlots = getAffectedSlots(
      apt.scheduledTime,
      apt.durationMinutes
    );
    
    console.log(`  📍 Appointment at ${apt.scheduledTime} (${apt.durationMinutes}min) affects slots:`, affectedSlots);
    
    for (const slot of affectedSlots) {
      const currentCount = occupancyMap.get(slot) || 0;
      occupancyMap.set(slot, currentCount + 1);
    }
  }
  
  console.log(`✅ Occupancy map built. Total slots with bookings: ${occupancyMap.size}`);
  return occupancyMap;
}

/**
 * Check if a new appointment can be booked
 * @param date - Date in YYYY-MM-DD format
 * @param time - Start time in HH:mm format
 * @param durationMinutes - Duration in minutes
 * @param existingAppointments - List of existing appointments
 * @param maxCapacity - Maximum cars per slot (default: 3)
 * @returns Booking availability info
 * 
 * @example
 * canBookAppointment("2025-10-28", "15:00", 120, appointments)
 * // Returns: { available: true, blockedSlots: [], reason: null }
 */
export function canBookAppointment(
  date: string,
  time: string,
  durationMinutes: number,
  existingAppointments: Appointment[],
  maxCapacity: number = 3,
  closeTime: string = "19:00"
): { available: boolean; blockedSlots: number[]; reason: string | null } {
  const occupancy = buildDailyOccupancy(date, existingAppointments);
  const requestedSlots = getAffectedSlots(time, durationMinutes);
  const blockedSlots: number[] = [];
  
  console.log(`🔍 Checking availability for ${time} (${durationMinutes}min)...`);
  console.log(`  Requested slots:`, requestedSlots.map(s => `${slotIndexToTime(s)} (${s})`));
  
  // Check if appointment extends past closing time
  const lastSlot = requestedSlots[requestedSlots.length - 1];
  const closeSlot = timeToSlotIndex(closeTime);
  
  if (lastSlot >= closeSlot) {
    console.log(`  ❌ Extends past closing time (${closeTime})`);
    return {
      available: false,
      blockedSlots: [],
      reason: `İşlem kapanış saatini (${closeTime}) geçiyor`
    };
  }
  
  // Check capacity for each affected slot
  for (const slot of requestedSlots) {
    const count = occupancy.get(slot) || 0;
    console.log(`  Slot ${slot} (${slotIndexToTime(slot)}): ${count}/${maxCapacity}`);
    
    if (count >= maxCapacity) {
      blockedSlots.push(slot);
      console.log(`    ❌ FULL`);
    }
  }
  
  const available = blockedSlots.length === 0;
  
  if (available) {
    console.log(`  ✅ All slots available - booking can proceed`);
  } else {
    console.log(`  ❌ ${blockedSlots.length} slots are full:`, blockedSlots.map(s => slotIndexToTime(s)));
  }
  
  return {
    available,
    blockedSlots,
    reason: available ? null : `Seçilen saat dilimi dolu: ${blockedSlots.map(s => slotIndexToTime(s)).join(", ")}`
  };
}

/**
 * Get available slots with capacity information for UI display
 * @param date - Date in YYYY-MM-DD format
 * @param durationMinutes - Duration of service in minutes
 * @param existingAppointments - List of existing appointments
 * @param openTime - Opening time (default: "09:00")
 * @param closeTime - Closing time (default: "19:00")
 * @param maxCapacity - Maximum cars per slot (default: 3)
 * @returns Array of slot information objects
 */
export function getAvailableSlotsWithCapacity(
  date: string,
  durationMinutes: number,
  existingAppointments: Appointment[],
  openTime: string = "09:00",
  closeTime: string = "19:00",
  maxCapacity: number = 3
): SlotInfo[] {
  const occupancy = buildDailyOccupancy(date, existingAppointments);
  const startSlot = timeToSlotIndex(openTime);
  const endSlot = timeToSlotIndex(closeTime);
  const slots: SlotInfo[] = [];
  
  console.log(`📅 Getting available slots for ${date} (${durationMinutes}min service)`);
  console.log(`  Operating hours: ${openTime} - ${closeTime} (slots ${startSlot}-${endSlot})`);
  
  for (let slot = startSlot; slot < endSlot; slot++) {
    const time = slotIndexToTime(slot);
    const { available } = canBookAppointment(
      date,
      time,
      durationMinutes,
      existingAppointments,
      maxCapacity,
      closeTime
    );
    
    const currentCapacity = occupancy.get(slot) || 0;
    
    slots.push({
      time,
      slotIndex: slot,
      capacity: currentCapacity,
      available,
      fullness: `${currentCapacity}/${maxCapacity}`
    });
  }
  
  const availableCount = slots.filter(s => s.available).length;
  console.log(`✅ Found ${availableCount}/${slots.length} available slots`);
  
  return slots;
}

/**
 * Get occupancy summary for a specific date
 * @param date - Date in YYYY-MM-DD format
 * @param appointments - List of appointments
 * @returns Summary statistics
 */
export function getOccupancySummary(date: string, appointments: Appointment[]) {
  const occupancy = buildDailyOccupancy(date, appointments);
  const slots = Array.from(occupancy.entries());
  
  const totalSlots = slots.length;
  const fullSlots = slots.filter(([_, count]) => count >= 3).length;
  const partialSlots = slots.filter(([_, count]) => count > 0 && count < 3).length;
  const totalCars = slots.reduce((sum, [_, count]) => sum + count, 0);
  
  return {
    date,
    totalSlots,
    fullSlots,
    partialSlots,
    emptySlots: totalSlots - fullSlots - partialSlots,
    totalCars,
    utilizationRate: totalSlots > 0 ? ((fullSlots / totalSlots) * 100).toFixed(1) : '0'
  };
}
